import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';

import { RestapiService } from '@app/services/restapi.service';
import { SurveysService } from '@app/services/surveys.service';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import {
  createTemplateDetails,
  deleteTemplate,
  loadAllUnitTypes,
  loadAllUnitTypesFailed,
  loadAllUnitTypesSuccess,
  loadAllUserTemplates,
  loadAllUserTemplatesFailed,
  loadAllUserTemplatesSuccess,
  removeAreaFromTemplate,
  updateTemplateDetails,
  updateTemplateDetailsFailed,
} from '@template/store/template.actions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

// TODO remove ALL direct API calls from this effect. Put it in ApiTemplateService

@Injectable()
export class TemplateEffects {
  loadAllUserTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllUserTemplates),
      mergeMap(() =>
        this.restApiService
          .getData<RestTemplateModel[]>(
            `inspections/template/list?filterUnavailableTemplates=true&doNotReturnContent=true`
          )
          .pipe(
            map(result => loadAllUserTemplatesSuccess({ listItems: result || [] })),
            catchError(err => {
              this.snackbarService.error(SnackbarErrorMessage.LoadingTemplatesList);
              return of(loadAllUserTemplatesFailed());
            })
          )
      )
    )
  );

  loadAllUnitTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllUnitTypes),
      exhaustMap(action =>
        this.restApiService.getData<string[]>(`properties/${action.propertyId}/unitTypes`).pipe(
          map(result => loadAllUnitTypesSuccess({ unitTypes: result || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingUnitTypes);
            return of(loadAllUnitTypesFailed());
          })
        )
      )
    )
  );

  updateTemplateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTemplateDetails),
      mergeMap(action =>
        this.restApiService.post<RestTemplateModel>(`inspections/template/edit`, action.entityDetails).pipe(
          mergeMap(templateDetails => [loadAllUserTemplates()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingTemplate);
            return of(updateTemplateDetailsFailed());
          })
        )
      )
    )
  );

  removeAreaFromTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeAreaFromTemplate),
      mergeMap(action =>
        this.surveyService.delete(action.areaId).pipe(
          mergeMap(() => [loadAllUserTemplates()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.WhileDeletingPropertyFromTemplate);
            return of(updateTemplateDetailsFailed());
          })
        )
      )
    )
  );

  createTemplateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createTemplateDetails),
      mergeMap(action =>
        this.restApiService.create<number>(`inspections/template/add`, action.entityDetails).pipe(
          tap(id =>
            this.router.navigate([AppRoutes.SETTINGS_TEMPLATE, id], { queryParamsHandling: 'preserve' }).then()
          ),
          mergeMap(() =>
            action.entityDetails.inspectionTemplateAreas
              ? action.entityDetails.inspectionTemplateAreas.map(s => this.surveyService.add({ ...s, id: 0 }))
              : of(undefined)
          ),
          mergeMap(() => [loadAllUserTemplates()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.WhileCreatingNewTemplate);
            return of(updateTemplateDetailsFailed());
          })
        )
      )
    )
  );

  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTemplate),
      mergeMap(action =>
        this.restApiService.post<number>('inspections/template/delete', action.id).pipe(
          map(() => loadAllUserTemplates()),
          catchError(err => {
            if (err?.error?.['ErrorCode'] === 'inspection_template_delete_unavailable') {
              this.snackbarService.error(SnackbarErrorMessage.RemovingTemplateInUse);
            } else {
              this.snackbarService.error(SnackbarErrorMessage.RemovingTemplate);
            }

            return of(updateTemplateDetailsFailed(), loadAllUserTemplates());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private restApiService: RestapiService,
    private surveyService: SurveysService,
    private snackbarService: SnackbarService,
    private router: Router
  ) {}
}
